// Kompletní zápis (per hod/frame) a výpočet skóre. Všechny typy se převedou na "efektivní ten-pin"
// (kde 1. hod = strike -> 10, spare -> součet 10) a pak jede jeden standardní scorer. Liší se jen
// spouštěč strike/spare (viz isStrike/isSpare). Ověřeno proti kontrolním případům (scratch/score.py).
//
// - Klasika:   strike = 10;          spare = a+b == 10
// - Devítkový: strike = 1. hod >= 9; spare = a+b == 10   (no-tap 9)
// - Osmičkový: strike = 1. hod >= 8; spare = a+b == 10   (no-tap 8)
// - Narubák:   strike = 1. hod == 0; spare = 2. hod == 0 (lowball; 0 = jako by spadlo 10)
//
// Skutečné shozené kuželky (9/10) zůstávají v zápisu -> no-tap strike je odlišitelný od čistého.

object Scoring {
    fun isStrike(type: String, b: Int): Boolean = when (type) {
        "nine" -> b >= 9
        "eight" -> b >= 8
        "narubak" -> b == 0
        else -> b == 10 // classic
    }

    fun isSpare(type: String, a: Int, b: Int): Boolean =
        if (type == "narubak") b == 0 else a + b == 10

    /** Efektivní hodnota 2. hodu (u narubák spare = 10 - a; jinak skutečné kuželky). */
    private fun eff2(type: String, a: Int, b: Int, spare: Boolean): Int =
        if (type == "narubak" && spare) 10 - a else b

    /** Převede raw hody jednoho rámce na efektivní ten-pin hody. `tenth` = 10. rámec (bonusové hody). */
    fun normFrame(type: String, throws: List<Int>, tenth: Boolean): List<Int> {
        if (throws.isEmpty()) return emptyList()
        val eff = mutableListOf<Int>()
        val b1 = throws[0]
        if (isStrike(type, b1)) {
            eff.add(10)
            if (tenth && throws.size > 1) {
                val b2 = throws[1]
                if (isStrike(type, b2)) {
                    eff.add(10)
                    if (throws.size > 2) {
                        val b3 = throws[2]
                        eff.add(if (isStrike(type, b3)) 10 else b3)
                    }
                } else if (throws.size > 2) {
                    val b3 = throws[2]
                    val sp = isSpare(type, b2, b3)
                    eff.add(b2); eff.add(eff2(type, b2, b3, sp))
                } else {
                    eff.add(b2)
                }
            }
        } else {
            val b2 = throws.getOrElse(1) { 0 }
            val sp = isSpare(type, b1, b2)
            eff.add(b1); eff.add(eff2(type, b1, b2, sp))
            if (tenth && sp && throws.size > 2) {
                val b3 = throws[2]
                eff.add(if (isStrike(type, b3)) 10 else b3)
            }
        }
        return eff
    }

    /** Standardní ten-pin součet z efektivních hodů (bezpečné indexování pro nedohranou hru). */
    private fun tenpin(rolls: List<Int>): Int {
        var total = 0
        var i = 0
        for (f in 0 until 10) {
            if (i >= rolls.size) break
            val r0 = rolls[i]
            val r1 = rolls.getOrElse(i + 1) { 0 }
            val r2 = rolls.getOrElse(i + 2) { 0 }
            when {
                r0 == 10 -> { total += 10 + r1 + r2; i += 1 }
                r0 + r1 == 10 -> { total += 10 + r2; i += 2 }
                else -> { total += r0 + r1; i += 2 }
            }
        }
        return total
    }

    /** Celkové skóre kompletního zápisu (frames = raw hody per rámec, 1..10 rámců). */
    fun total(type: String, frames: List<List<Int>>): Int {
        val rolls = mutableListOf<Int>()
        frames.forEachIndexed { idx, fr -> rolls.addAll(normFrame(type, fr, tenth = idx == 9)) }
        return tenpin(rolls)
    }

    /** Kumulativní součet po každém z 10 rámců (pro vykreslení zápisu). */
    fun frameCumulative(type: String, frames: List<List<Int>>): List<Int> {
        val rolls = mutableListOf<Int>()
        frames.forEachIndexed { idx, fr -> rolls.addAll(normFrame(type, fr, tenth = idx == 9)) }
        val cum = mutableListOf<Int>()
        var total = 0
        var i = 0
        for (f in 0 until 10) {
            if (i >= rolls.size) { cum.add(total); continue }
            val r0 = rolls[i]; val r1 = rolls.getOrElse(i + 1) { 0 }; val r2 = rolls.getOrElse(i + 2) { 0 }
            when {
                r0 == 10 -> { total += 10 + r1 + r2; i += 1 }
                r0 + r1 == 10 -> { total += 10 + r2; i += 2 }
                else -> { total += r0 + r1; i += 2 }
            }
            cum.add(total)
        }
        return cum
    }

    /** Only settled scores: pending strike/spare bonuses stay unknown instead of becoming zeros. */
    fun resolvedFrameCumulative(type: String, frames: List<List<Int>>): List<Int?> {
        val rolls = mutableListOf<Int>()
        for ((index, raw) in frames.take(10).withIndex()) {
            val first = raw.firstOrNull() ?: break
            val strike = isStrike(type, first)
            val spare = !strike && raw.size >= 2 && isSpare(type, first, raw[1])
            val required = if (index == 9 && (strike || spare)) 3 else if (strike) 1 else 2
            val available = raw.take(required)
            rolls.addAll(if (!strike && available.size == 1) available else normFrame(type, available, index == 9))
            if (raw.size < required) break
        }
        val result = MutableList<Int?>(10) { null }
        var offset = 0
        var total = 0
        for (frame in 0 until 10) {
            val first = rolls.getOrNull(offset) ?: break
            val second = rolls.getOrNull(offset + 1) ?: break
            val bonus = first == 10 || first + second == 10
            val third = if (bonus) rolls.getOrNull(offset + 2) ?: break else 0
            total += if (first == 10) 10 + second + third else first + second + third
            result[frame] = total
            offset += if (first == 10) 1 else 2
        }
        return result
    }

    /** Značky rámce pro vykreslení: X (strike), / (spare), - (0), číslo. `tenth` = 10. rámec. */
    fun frameMarks(type: String, throws: List<Int>, tenth: Boolean): List<String> {
        if (throws.isEmpty()) return emptyList()
        val b1 = throws[0]
        if (!tenth) {
            if (isStrike(type, b1)) return listOf("X", "")
            val m1 = if (b1 == 0) "-" else "$b1"
            val b2 = throws.getOrNull(1) ?: return listOf(m1, "")
            return listOf(m1, if (isSpare(type, b1, b2)) "/" else if (b2 == 0) "-" else "$b2")
        }
        val marks = mutableListOf<String>()
        if (isStrike(type, b1)) {
            marks.add("X")
            val b2 = throws.getOrNull(1) ?: return marks
            if (isStrike(type, b2)) {
                marks.add("X")
                throws.getOrNull(2)?.let { marks.add(if (isStrike(type, it)) "X" else if (it == 0) "-" else "$it") }
            } else {
                marks.add(if (b2 == 0) "-" else "$b2")
                throws.getOrNull(2)?.let { b3 -> marks.add(if (isSpare(type, b2, b3)) "/" else if (b3 == 0) "-" else "$b3") }
            }
        } else {
            marks.add(if (b1 == 0) "-" else "$b1")
            val b2 = throws.getOrNull(1) ?: return marks
            val spare = isSpare(type, b1, b2)
            marks.add(if (spare) "/" else if (b2 == 0) "-" else "$b2")
            if (spare) throws.getOrNull(2)?.let { b3 -> marks.add(if (isStrike(type, b3)) "X" else if (b3 == 0) "-" else "$b3") }
        }
        return marks
    }
}
